import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum-slim';
import type { LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum as slimDatadogRum } from '@datadog/browser-rum-slim';
import { datadogLogs } from '@datadog/browser-logs';
import { getMarket } from '@clearscore-group/lib.helpers.market';
import { getPersonCookie, getCookie, constants } from '@clearscore-group/lib.helpers.cookies';
import type { Market } from '@clearscore-group/lib.constants.market';

export type AppKey =
    | 'webapp'
    | 'drivescoreWebapp'
    | 'drivescoreWebsite'
    | 'clearscoreWebsite'
    | 'clearscore-website'
    | 'group-website'
    | 'clearer-webapp'
    | 'clearscore-webapp'
    | 'csew-webapp';

export const ddApps = {
    webapp: 'clearscore-webapp',
    drivescoreWebapp: 'drivescore-webapp',
    drivescoreWebsite: 'drivescore-website',
    clearscoreWebsite: 'clearscore-website',
    'clearscore-website': 'clearscore-website',
    'group-website': 'group-website',
    'clearer-webapp': 'clearer-webapp',
    'clearscore-webapp': 'clearscore-webapp',
    'csew-webapp': 'csew-webapp',
} as const satisfies Record<AppKey, string>;

export type DDAppKey = keyof typeof ddApps;

export const ddApplicationIds: Record<DDAppKey, string> = {
    webapp: '3eb18c69-38e0-4095-80e2-78aab47831ae',
    drivescoreWebapp: '4f347bd3-31d2-41c0-a304-4b542321bfaa',
    drivescoreWebsite: '97a55d2a-3577-4acd-8cde-b7b4a2eb6f8c',
    // The Gatsby marketing site (apps/website, `${SITE}Website` key) and the SSR
    // app (`clearscore-website` SERVICE_NAME key) report to intentionally separate
    // RUM apps: "ClearScore Website" vs "ClearScore Website SSR". Keep them distinct.
    clearscoreWebsite: '637b8cb2-1223-40ce-bf0a-7f6dbea7a188',
    'clearscore-website': '80c1faa4-63e0-4c43-ae37-a7a14e09e02d',
    'group-website': '6031bc1e-2a87-4fbc-8325-35107239f390',
    'clearer-webapp': '4bdbfc94-01e0-44fb-aafb-30df4819cd90',
    'clearscore-webapp': '824de53d-84e2-4638-b470-359fde62b837',
    // TODO(OF-1275): replace after #ask-it creates Datadog RUM app `csew-webapp`
    'csew-webapp': 'c0ee5000-0000-4000-8000-0000c5e57700',
} as const;

export const ddClientTokens: Record<DDAppKey, string> = {
    webapp: 'pub29a684674f4826c6737542cce2219201',
    drivescoreWebapp: 'pub8b95eb1a276fc5e5d1e4d5a928d49b2b',
    drivescoreWebsite: 'pubf94cd5795b86d1abd74dbbaae949ac7b',
    // "ClearScore Website" (Gatsby) client token; distinct from the SSR app below.
    clearscoreWebsite: 'pubce82de9f9e4cbf8ee2f2a8ba0db399bc',
    'clearscore-website': 'pub1b6636d268096d9021c93be23e1ef159',
    'group-website': 'pubf4b21d99bf416a68ede721806887dea0',
    'clearer-webapp': 'pub48a60a8ecfa645614793c5504ad55a4b',
    'clearscore-webapp': 'puba9e6920f57b4d34db39de58249cbb106',
    // TODO(OF-1275): replace with real client token from Datadog RUM app `csew-webapp`
    'csew-webapp': 'pubc0e57e770000000000000000000000',
} as const;

export type DDAppValue = (typeof ddApps)[DDAppKey];
type DDApplicationIdValue = (typeof ddApplicationIds)[DDAppKey];
type DDClientToken = (typeof ddClientTokens)[DDAppKey];
type DDEnvironment = 'ci' | 'staging' | 'production';
type DDRumInstance = typeof slimDatadogRum;
type DDInstance = DDRumInstance | typeof datadogLogs;

export const DD_DEFAULT_SESSION_SAMPLE_RATE = 0;
export const DD_DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DD_DEFAULT_TRACE_SAMPLE_RATE = 10;
const DD_CLEARSCORE_WEBSITE_PRODUCTION_SESSION_SAMPLE_RATE = 5;
const DD_SITE = 'datadoghq.eu';
const DD_DEFAULT_PRIVACY_LEVEL = 'mask';

const shouldUseFullRumClient = (sessionReplaySampleRate: RumInitConfiguration['sessionReplaySampleRate']) =>
    Number(sessionReplaySampleRate) > 0;

const resolveSessionSampleRate = ({
    app,
    mappedEnv,
    sessionSampleRate,
}: {
    app: DDAppValue;
    mappedEnv: string;
    sessionSampleRate: RumInitConfiguration['traceSampleRate'];
}) => {
    if (
        app === ddApps['clearscore-website'] &&
        mappedEnv.endsWith('-production') &&
        Number(sessionSampleRate) === DD_DEFAULT_SESSION_SAMPLE_RATE
    ) {
        return DD_CLEARSCORE_WEBSITE_PRODUCTION_SESSION_SAMPLE_RATE;
    }

    return sessionSampleRate;
};

const delegateSlimRumClientToFullRumClient = (fullDatadogRum: DDRumInstance) => {
    // Several SDUI modules import the slim singleton directly for addError().
    // When replay is enabled the full SDK owns the initialized RUM strategy, so
    // keep those existing slim imports pointed at the active client.
    Object.assign(slimDatadogRum, fullDatadogRum);
};

/**
 * Hosts where the Datadog Browser RUM SDK injects `x-datadog-trace-id` /
 * `x-datadog-parent-id` headers so backend APM traces (identity-surface,
 * sdui-api, etc.) can be linked back to the RUM session that produced them.
 *
 * Each entry is a prefix match. We list both `/_sdui` (the SDUI hydration
 * endpoint the browser POSTs to) and `/api` (other XHR/fetch traffic) for
 * each SSR webapp origin in production and staging.
 *
 * DriveScore has no SSR/SDUI app, so only `/api` is listed for its webapp
 * origins (including the `webviews` host the native apps embed).
 */
export const DD_ALLOWED_TRACING_URLS = [
    'https://app.clearscore.com/_sdui',
    'https://app.clearscore.com/api',
    'https://app-stg.cs55.co.uk/_sdui',
    'https://app-stg.cs55.co.uk/api',
    'https://app.drivescore.com/api',
    'https://webviews.drivescore.com/api',
    'https://ds-stg.cs55.co.uk/api',
    'https://drivescore-webviews-stg.cs55.co.uk/api',
];

export const ADDRESS_OPERATION_EVENT_NAME = 'address_operation_result';
export const ADDRESS_RENDER_WATCHDOG_MS = 5_000;

const SAFE_ADDRESS_OPERATION_FIELDS = new Set([
    'schema_version',
    'market',
    'route',
    'flow_id',
    'sdui_response_id',
    'operation',
    'change_type',
    'phase',
    'outcome',
    'failure_reason',
    'lookup_provider',
    'input_source',
    'previous_count',
    'new_count',
    'address_index',
    'rendered',
    'duration_ms',
    'http_status_code',
    'downstream_service',
    'downstream_resource',
    'downstream_status_code',
    'environment',
    'release',
]);

interface AddressRenderWatchdog {
    timeout: ReturnType<typeof setTimeout>;
    properties: Record<string, string | number | boolean>;
}

const addressRenderWatchdogs = new Map<string, AddressRenderWatchdog>();

/** @internal Test-only state isolation for the address render watchdog. */
export const resetAddressRenderWatchdogsForTesting = () => {
    for (const watchdog of addressRenderWatchdogs.values()) clearTimeout(watchdog.timeout);
    addressRenderWatchdogs.clear();
};

const isSafeRumValue = (value: unknown): value is string | number | boolean =>
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

export const sanitiseAddressOperationRumProperties = (
    properties: Record<string, unknown>,
): Record<string, string | number | boolean> => {
    const safeProperties: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(properties)) {
        if (SAFE_ADDRESS_OPERATION_FIELDS.has(key) && isSafeRumValue(value)) safeProperties[key] = value;
    }
    return safeProperties;
};

type AddressRenderOperation = 'create' | 'edit';

const normaliseAddressOperationCorrelationValue = (value: unknown) => {
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
};

const getAddressRenderOperation = (value: unknown): AddressRenderOperation | undefined =>
    value === 'create' || value === 'edit' ? value : undefined;

const addressOperationKey = (properties: Record<string, unknown>): string | undefined => {
    const flowId = normaliseAddressOperationCorrelationValue(properties.flow_id);
    const sduiResponseId = normaliseAddressOperationCorrelationValue(properties.sdui_response_id);
    const operation = getAddressRenderOperation(properties.operation);
    const addressIndex = normaliseAddressOperationCorrelationValue(properties.address_index);

    if (!flowId || !sduiResponseId || !operation || addressIndex === undefined) return undefined;

    return JSON.stringify([flowId, sduiResponseId, operation, addressIndex]);
};

const findAddressRenderWatchdog = (properties: Record<string, unknown>) => {
    const exactKey = addressOperationKey(properties);
    if (exactKey) {
        const exactWatchdog = addressRenderWatchdogs.get(exactKey);
        if (exactWatchdog) return { key: exactKey, watchdog: exactWatchdog };
    }

    const operation = getAddressRenderOperation(properties.operation);
    const addressIndex = normaliseAddressOperationCorrelationValue(properties.address_index);
    if (!operation || addressIndex === undefined) return undefined;

    const flowId = normaliseAddressOperationCorrelationValue(properties.flow_id);
    const sduiResponseId = normaliseAddressOperationCorrelationValue(properties.sdui_response_id);
    const matches: { key: string; watchdog: AddressRenderWatchdog }[] = [];

    for (const [key, watchdog] of addressRenderWatchdogs.entries()) {
        const watchdogFlowId = normaliseAddressOperationCorrelationValue(watchdog.properties.flow_id);
        const watchdogResponseId = normaliseAddressOperationCorrelationValue(watchdog.properties.sdui_response_id);
        if (
            watchdog.properties.operation === operation &&
            normaliseAddressOperationCorrelationValue(watchdog.properties.address_index) === addressIndex &&
            (!properties.market || watchdog.properties.market === properties.market) &&
            (!flowId || watchdogFlowId === flowId) &&
            (!sduiResponseId || watchdogResponseId === sduiResponseId)
        ) {
            matches.push({ key, watchdog });
        }
    }

    return matches.length === 1 ? matches[0] : undefined;
};

/**
 * Mirrors only the bounded address-operation contract into RUM. A fully correlated legacy flow
 * success starts a five-second render watchdog; the matching ui_render event cancels it. Incomplete
 * correlation is still mirrored without a watchdog so telemetry cannot cross-link separate flows.
 * Raw address/provider/session values are excluded by construction rather than relying on callers
 * to remove them.
 */
export const mirrorAddressOperationToRum = (
    properties: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined => {
    try {
        const safeProperties = sanitiseAddressOperationRumProperties(properties);
        if (safeProperties.market !== 'ca') return undefined;

        const isTrue = (value: unknown) => value === true || value === 'true';

        if (
            (safeProperties.operation === 'create' || safeProperties.operation === 'edit') &&
            safeProperties.phase === 'materialisation' &&
            safeProperties.outcome === 'success'
        ) {
            const key = addressOperationKey(safeProperties);
            if (key) {
                const existingWatchdog = addressRenderWatchdogs.get(key);
                if (existingWatchdog) clearTimeout(existingWatchdog.timeout);

                addressRenderWatchdogs.set(key, {
                    properties: safeProperties,
                    timeout: setTimeout(() => {
                        addressRenderWatchdogs.delete(key);
                        try {
                            slimDatadogRum.addAction(ADDRESS_OPERATION_EVENT_NAME, {
                                ...safeProperties,
                                phase: 'ui_render',
                                outcome: 'error',
                                failure_reason: 'address_not_rendered',
                                rendered: false,
                            });
                        } catch {
                            // Monitoring failures must never affect the registration session.
                        }
                    }, ADDRESS_RENDER_WATCHDOG_MS),
                });
            }
        }

        if (safeProperties.phase === 'ui_render' && isTrue(safeProperties.rendered)) {
            const match = findAddressRenderWatchdog(safeProperties);
            if (!match) return undefined;

            clearTimeout(match.watchdog.timeout);
            addressRenderWatchdogs.delete(match.key);
            const resolvedProperties = {
                ...match.watchdog.properties,
                ...safeProperties,
                flow_id: match.watchdog.properties.flow_id,
                sdui_response_id: match.watchdog.properties.sdui_response_id,
            };
            slimDatadogRum.addAction(ADDRESS_OPERATION_EVENT_NAME, resolvedProperties);
            return resolvedProperties;
        }

        slimDatadogRum.addAction(ADDRESS_OPERATION_EVENT_NAME, safeProperties);
        return safeProperties;
    } catch {
        // Address monitoring is deliberately fail-open.
        return undefined;
    }
};

const environmentMap: Record<string, DDEnvironment> = {
    ci: 'ci',
    stg: 'staging',
    prod: 'production',
} as const;

const uuidFunctionMap: Record<DDAppKey, () => string | undefined> = {
    webapp: getPersonCookie,
    drivescoreWebapp: getPersonCookie,
    drivescoreWebsite: getPersonCookie,
    clearscoreWebsite: getPersonCookie,
    'clearscore-website': getPersonCookie,
    'group-website': getPersonCookie,
    'clearer-webapp': () => undefined,
    'clearscore-webapp': getPersonCookie,
    'csew-webapp': getPersonCookie,
} as const;

const getUuid = (clientToken: string) => {
    const app = Object.keys(ddClientTokens).find((key) => ddClientTokens[key as DDAppKey] === clientToken);
    return uuidFunctionMap[app as DDAppKey]();
};

const setUser = (clientToken: string, dataDogInstance: DDInstance) => {
    let uuid;
    try {
        uuid = getUuid(clientToken);
        if (typeof uuid === 'string') {
            dataDogInstance.setUser({ id: uuid });
        } else {
            throw new Error('Invalid UUID');
        }
    } catch (e) {
        // Don't set uuid.
    }
};

/**
 * SSR apps inject `window.ENV` from the document (see sdui-app root.tsx).
 * We avoid augmenting `Window` in global.d.ts — that collides with
 * `@clearscore-group/lib.sdui-modules.sdui-app-env` (TS2687 identical modifiers).
 */
interface InjectedClientEnv {
    ENV?: string;
    MARKET?: string;
    COMMIT_REF?: string;
}

const isBrowser = () => typeof window !== 'undefined';

function getInjectedEnv(): InjectedClientEnv | undefined {
    if (!isBrowser()) {
        return undefined;
    }
    return (window as Window & { ENV?: InjectedClientEnv }).ENV;
}

function getRelease() {
    if (window.__cs__?.release?.env) {
        // Standard Applications
        return environmentMap[window.__cs__.release.env];
    }

    const injected = getInjectedEnv();
    if (injected?.ENV) {
        // SSR-hydrated clients (window.ENV from inline script)
        return environmentMap[injected.ENV];
    }

    return undefined;
}

function getMappedEnv(market: Market | null) {
    if (!market) {
        return null;
    }
    return `${market}-${getRelease()}`;
}

function calculateMarket() {
    let market = getMarket();

    const injected = getInjectedEnv();
    if (!market && injected?.MARKET) {
        market = injected.MARKET as Market;
    }

    if (market === undefined) {
        // Fallback to the browser locale from the i18next-browser-languagedetector language detector
        // (packages/clearscore-group/libs/redux/cs-provider/src/lib/i18n.ts)
        // as a best guess for market on the login screen.
        const currentLanguage = getCookie(constants.CS_CURRENT_LANGUAGE);

        if (currentLanguage === undefined) {
            return null;
        }

        // We currently only have `en-${market}` languages; if this ever changes, update this.
        [, market] = currentLanguage.split('-');
    }

    return market || null;
}

type ErrorMessage = string;
type Reason = string;

export const errorMessagesToFilter: Record<ErrorMessage, Reason> = {
    'Object Not Found Matching Id:': 'Login page issue',
    ResizeObserver: 'Not polyfilled',
    'Script error.':
        'This is from CORS errors from 3rd party scripts (https://server-side-tagging / Persona) in Safari. TODO: Fix the CORS',
    'Error sending segment performance metrics': 'Segment performance metrics failure (3rd party)',
    "Cannot read properties of null (reading 'reason')": 'Login epic type error isMigrationType',
    'Failed to fetch': 'Segment 3rd party',
    'Failed to load Analytics.js': 'Segment 3rd party',
    'Failed to import: //cdn2-fs.clearscore.com/': 'Flux lazy loading failure',
    'Error css file. src: //cdn2-fs.clearscore.com/': 'Flux lazy loading css failure',
    'Load failed': 'Segment 3rd party',
    'Uncaught "Empty reason"': 'Not sure but across all verticals',
    'Fetch is aborted':
        'Benign AbortError (WebKit wording): react-router aborts its in-flight navigation fetch when a new navigation interrupts it, e.g. CHANGE_SCOPE after SSR login (SDUI-1059)',
    'The user aborted a request.': 'Benign AbortError (Chromium wording), same cause as "Fetch is aborted" (SDUI-1059)',
    // A GTM-managed Braze tag calls `changeUser()` on pageview before an external id exists, so the
    // Braze SDK logs a handled `console.error`. The true source is the GTM container (GTM-KLZ43FV),
    // not this repo. The trailing SDK version (e.g. " (v4.8.3)") is omitted so the match survives
    // Braze SDK upgrades.
    'Braze SDK Error: changeUser requires a non-empty userId.':
        'Braze tag fires before an external id exists (GTM-KLZ43FV)',
    // The same GTM Braze tag cannot use IndexedDB (private browsing, storage eviction) and registers a
    // web-push service worker that app.clearscore.com has never served (404).
    'on indexedDB AppboyServiceWorkerAsyncStorage': 'Braze tag cannot use IndexedDB (GTM-KLZ43FV)',
    'Braze SDK Error: ServiceWorker registration failed':
        'Braze tag registers a service worker we do not serve (GTM-KLZ43FV)',
    // A GTM Branch tag whose API calls fail, usually because an ad blocker blocks them.
    '[Branch GTM logEvent Error]': 'GTM Branch tag request failed or blocked',
    // Browser extensions: crypto wallets, extension messaging and password managers.
    'Failed to connect to MetaMask': 'MetaMask wallet extension',
    'Internal JSON-RPC error': 'Crypto wallet extension',
    'Invalid call to runtime.sendMessage(). Tab not found.': 'Browser extension messaging',
    'Error in invocation of runtime.sendMessage': 'Browser extension messaging',
    'window.ethereum': 'Crypto wallet extension',
    __RoboForm__: 'RoboForm password manager extension',
    // Scripts injected by in-app browsers, readers and WebView hosts. Our documents define none of these.
    'WrappedBody.rootNode': 'In-app browser content extraction script',
    updateAndCleanExtractedContent: 'In-app browser content extraction script',
    'window.__KVARS__': 'Injected in-app browser or extension script',
    'Java object is gone': 'Android WebView bridge torn down by the host app',
    'dbankcloud.com': "Huawei Browser's built-in page translation request",
    // The Amplitude SDK falls back to the local config we pass to init() when the remote config fetch fails.
    'Failed to generate joined config': 'Amplitude SDK remote config fetch blocked or offline',
    // A browser intervention report, not an error: the browser ignored a preventDefault() because the page was
    // already scrolling. RUM collects these from the Reporting API; the ones we see come from the Braze SDK's
    // in-app message touch handlers on /protect.
    'Ignored attempt to cancel a touchmove event with cancelable=false': 'Browser intervention report (Braze SDK)',
    'Error in input stream':
        'Firefox wording of a fetch body cut off mid-read, e.g. by a navigation, with no stack (same family as SDUI-1059)',
};

// First-party errors that contain a filtered substring. Chromium words a chunk-load failure as
// "TypeError: Failed to fetch dynamically imported module: <url>", which 'Failed to fetch' would otherwise drop.
const errorMessagesToKeep: ErrorMessage[] = ['Failed to fetch dynamically imported module'];

// Errors from scripts injected into the page at runtime (in-app-browser bridges,
// wallet / translation / accessibility extensions) surface in WebViews with an
// anonymous-only stack and no resolvable source file, e.g.
//   TypeError: Cannot read properties of null (reading 'src')
//     at <anonymous> @ <anonymous>:1:60
// First-party code always has at least one frame pointing at a real source file
// (an http(s) URL or a .js/.ts file), so an anonymous-only stack is a safe signal
// that the error did not originate in our bundle. We filter on the stack rather than
// the message because these messages ("...reading 'src'", "...reading 'querySelector'")
// are too generic to filter by substring without risking real first-party errors (SDUI-1091).
const SOURCE_FILE_FRAME = /(https?:\/\/|\.[mc]?[jt]sx?\b)/i;

export const isInjectedScriptError = (stack?: string): boolean => {
    if (!stack) {
        return false;
    }
    const frames = stack.split('\n').filter((line) => /^\s*at\s/.test(line));
    return frames.length > 0 && frames.every((line) => line.includes('<anonymous>')) && !SOURCE_FILE_FRAME.test(stack);
};

export const beforeSend = (event: RumEvent) => {
    if (event.type === 'error' && event.error) {
        if (isInjectedScriptError(event.error.stack)) {
            return false;
        }

        const referrerUrl = event.view?.referrer || document.referrer || '';

        if (referrerUrl) {
            let refHost = '';
            try {
                refHost = new URL(referrerUrl).host;
            } catch {
                return false;
            }

            const allowedHosts = new Set([
                'app.clearscore.com',
                'app-stg.cs55.co.uk',
                'app.drivescore.com',
                'webviews.drivescore.com',
                'ds-stg.cs55.co.uk',
                'drivescore-webviews-stg.cs55.co.uk',
            ]);

            if (!allowedHosts.has(refHost)) {
                return false;
            }
        }

        if (errorMessagesToKeep.some((errorMessage) => event.error.message.includes(errorMessage))) {
            return true;
        }

        return !Object.keys(errorMessagesToFilter).some((errorMessage) => event.error.message.includes(errorMessage));
    }

    return true;
};

export const initialiseDataDogRUM = ({
    app,
    applicationId,
    clientToken,
    sessionSampleRate = DD_DEFAULT_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate = DD_DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
    env: envOverride,
}: {
    app: DDAppValue;
    applicationId: DDApplicationIdValue;
    clientToken: DDClientToken;
    sessionSampleRate?: RumInitConfiguration['traceSampleRate'];
    sessionReplaySampleRate?: RumInitConfiguration['sessionReplaySampleRate'];
    /** Explicit env string (e.g. 'gb-production'). Bypasses getRelease()/calculateMarket() auto-detection. */
    env?: string;
}): Promise<void> | undefined => {
    if (!isBrowser()) {
        return;
    }

    if (!envOverride && !getRelease()) {
        return;
    }

    const market = calculateMarket();
    const mappedEnv = envOverride ?? getMappedEnv(market);

    if (mappedEnv && !window.DD_RUM?.getInternalContext() && !mappedEnv.includes('ci')) {
        const resolvedSessionSampleRate = resolveSessionSampleRate({ app, mappedEnv, sessionSampleRate });

        const initialiseRumClient = (datadogRum: DDRumInstance) => {
            datadogRum.init({
                applicationId,
                clientToken,
                site: DD_SITE,
                service: app,
                env: mappedEnv,
                version: window.__cs__?.release?.core ?? getInjectedEnv()?.COMMIT_REF,
                sessionSampleRate: resolvedSessionSampleRate,
                sessionReplaySampleRate,
                traceSampleRate: DD_DEFAULT_TRACE_SAMPLE_RATE,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: DD_DEFAULT_PRIVACY_LEVEL,
                beforeSend,
                enablePrivacyForActionName: true,
                startSessionReplayRecordingManually: true,
                trackingConsent: 'granted',
                allowedTracingUrls: DD_ALLOWED_TRACING_URLS,
            });
            setUser(clientToken, datadogRum);
        };

        if (shouldUseFullRumClient(sessionReplaySampleRate)) {
            return import('@datadog/browser-rum').then(({ datadogRum: fullDatadogRum }) => {
                delegateSlimRumClientToFullRumClient(fullDatadogRum);
                initialiseRumClient(fullDatadogRum);
            });
        }

        initialiseRumClient(slimDatadogRum);
    }
};

export const initialiseDataDogLogs = (
    clientToken: DDClientToken,
    sessionSampleRate: LogsInitConfiguration['sessionSampleRate'] = DD_DEFAULT_SESSION_SAMPLE_RATE,
) => {
    if (!isBrowser()) {
        return;
    }

    if (!getRelease()) {
        return;
    }

    const market = calculateMarket();
    const mappedEnv = getMappedEnv(market);

    if (mappedEnv && !window.DD_LOGS?.getInternalContext() && !mappedEnv.includes('ci')) {
        datadogLogs.init({
            clientToken,
            site: DD_SITE,
            forwardErrorsToLogs: true,
            sessionSampleRate,
            usePciIntake: true,
            env: mappedEnv,
        });
        setUser(clientToken, datadogLogs);
    }
};
